import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, ChevronRight, SkipForward, CircleDot } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { CLASSES } from "@/lib/rpg";
import { btnPrimary, btnGhost, btnSecondary, label } from "@/lib/styles";

const LEVELS = [
  { id: "beginner", title: "Beginner", desc: "I have used ChatGPT or similar, but I do not really know how it works.", tier: "Tier 0" },
  { id: "intermediate", title: "Intermediate", desc: "I understand the basics and have written prompts or used an AI API.", tier: "Tier 1-2" },
  { id: "advanced", title: "Advanced", desc: "I have built AI features and worked with things like RAG or fine-tuning.", tier: "Tier 3" },
  { id: "top_1", title: "Top 1%", desc: "I design and ship production AI systems and could interview others.", tier: "Tier 4" },
];

const CLUSTERS = [
  { id: "basics", label: "AI basics and how LLMs work" },
  { id: "prompting", label: "Prompting" },
  { id: "rag", label: "RAG and retrieval" },
  { id: "evaluation", label: "Evaluation and reliability" },
  { id: "agents", label: "Agents" },
  { id: "finetuning", label: "Fine-tuning" },
  { id: "production", label: "Production and cost" },
  { id: "systemdesign", label: "System design" },
];

export default function Onboarding() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [charClass, setCharClass] = useState(null);
  const [level, setLevel] = useState(null);
  const [focus, setFocus] = useState([]);
  const [known, setKnown] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleFocus = (id) => {
    setKnown((k) => k.filter((x) => x !== id));
    setFocus((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  };
  const toggleKnown = (id, e) => {
    e.stopPropagation();
    setFocus((f) => f.filter((x) => x !== id));
    setKnown((k) => (k.includes(id) ? k.filter((x) => x !== id) : [...k, id]));
  };

  const save = async (skipped) => {
    setSaving(true);
    try {
      const payload = skipped
        ? { placement_level: "beginner", focus_areas: [], known_areas: [], character_class: charClass || "sage" }
        : { placement_level: level || "beginner", focus_areas: focus, known_areas: known, character_class: charClass || "sage" };
      const { data } = await api.post("/auth/placement", payload);
      setUser(data);
      toast.success(skipped ? "Placement skipped" : "Your legend begins");
      navigate("/curriculum");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl bg-navy-surface/90 border border-zinc-800 rounded-xl p-8 lg:p-10 relative">
        <div className="flex items-center justify-between mb-2">
          <span className={label}>quick setup · step {step} of 3</span>
          <button data-testid="onboarding-skip" onClick={() => save(true)} className={btnGhost}><SkipForward size={14} /> Skip</button>
        </div>

        {/* STEP 1 — CLASS */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="s1">
            <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight mb-1 text-zinc-100">Choose your class</h1>
            <p className="text-sm text-zinc-500 mb-6">Pick the path that calls to you. (You can switch later in your account.)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CLASSES.map((c) => (
                <button
                  key={c.id}
                  data-testid={`class-${c.id}`}
                  onClick={() => setCharClass(c.id)}
                  className={`text-left border rounded-lg p-4 transition-all flex gap-3 items-center ${charClass === c.id ? "border-gold/70 bg-gold/5 shadow-[0_0_22px_-6px_rgba(217,164,65,0.5)]" : "border-zinc-800 hover:border-zinc-700"}`}
                >
                  <img src={c.sigil} alt="" className="w-14 h-14 shrink-0 select-none pointer-events-none" style={{ WebkitMaskImage: "radial-gradient(circle,#000 60%,transparent 72%)", maskImage: "radial-gradient(circle,#000 60%,transparent 72%)" }} />
                  <div>
                    <p className="font-serif font-bold text-zinc-100">{c.name}</p>
                    <p className={`text-[11px] uppercase tracking-wider ${c.tint}`}>{c.title}</p>
                    <p className="text-xs text-zinc-500 mt-1 leading-snug">{c.blurb}</p>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-8 flex justify-end">
              <button data-testid="onboarding-next-class" disabled={!charClass} onClick={() => setStep(2)} className={btnPrimary}>Next <ChevronRight size={16} /></button>
            </div>
          </motion.div>
        )}

        {/* STEP 2 — PLACEMENT */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="s2">
            <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight mb-1 text-zinc-100">Where are you starting from?</h1>
            <p className="text-sm text-zinc-500 mb-6">Place yourself honestly. We drop you at the right rank.</p>
            <div className="space-y-3">
              {LEVELS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`placement-level-${l.id}`}
                  onClick={() => setLevel(l.id)}
                  className={`w-full text-left border p-4 rounded-md transition-all flex items-start gap-3 ${level === l.id ? "border-gold/70 bg-gold/5" : "border-zinc-800 hover:border-zinc-700"}`}
                >
                  <CircleDot size={18} className={level === l.id ? "text-gold mt-0.5" : "text-zinc-700 mt-0.5"} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-serif font-bold text-zinc-100">{l.title}</span>
                      <span className="text-[10px] uppercase tracking-wider text-gold">{l.tier}</span>
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">{l.desc}</p>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-8 flex justify-between">
              <button data-testid="onboarding-back-1" onClick={() => setStep(1)} className={btnSecondary}>Back</button>
              <button data-testid="onboarding-next" disabled={!level} onClick={() => setStep(3)} className={btnPrimary}>Next <ChevronRight size={16} /></button>
            </div>
          </motion.div>
        )}

        {/* STEP 3 — FOCUS */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="s3">
            <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight mb-1 text-zinc-100">What do you want to work on?</h1>
            <p className="text-sm text-zinc-500 mb-6">Tap to focus. Tap "solid" to deprioritize areas you already know.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CLUSTERS.map((c) => {
                const isFocus = focus.includes(c.id);
                const isKnown = known.includes(c.id);
                return (
                  <div
                    key={c.id}
                    data-testid={`focus-${c.id}`}
                    onClick={() => toggleFocus(c.id)}
                    className={`cursor-pointer border rounded-md p-3 transition-all ${isFocus ? "border-gold/70 bg-gold/5" : isKnown ? "border-zinc-700 bg-zinc-900/50 opacity-60" : "border-zinc-800 hover:border-zinc-700"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        {isFocus && <Check size={14} className="text-gold" />}
                        {c.label}
                      </span>
                    </div>
                    <button
                      data-testid={`known-${c.id}`}
                      onClick={(e) => toggleKnown(c.id, e)}
                      className={`mt-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border transition-colors ${isKnown ? "border-yellow-500/60 text-yellow-400 bg-yellow-950/30" : "border-zinc-700 text-zinc-500 hover:text-zinc-300"}`}
                    >
                      {isKnown ? "✓ already solid" : "mark solid"}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 flex justify-between">
              <button data-testid="onboarding-back" onClick={() => setStep(2)} className={btnSecondary}>Back</button>
              <button data-testid="onboarding-finish" disabled={saving} onClick={() => save(false)} className={btnPrimary}>{saving ? "saving..." : "Finish & enter"}</button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
